/*
 * 3D-Viewer Deckenlampen-PDP (Spec docs/2026-10-09-decke-3d-viewer-design.md,
 * Plan docs/2026-10-09-decke-3d-viewer-plan.md).
 *
 * Teil 1, reine Funktionen ohne DOM: Anordnungen, Startzustand, Uebergaenge,
 * Event-Payloads, Masse, Kamera, Hinweistext. Laeuft in Node (module.exports,
 * tests/decke-3d-viewer.test.cjs) und im Browser als window.Moonou3D.
 * Teil 2 (Browser: Popup, Laden der Runtime, Steuerung, Tracking, Einbau von
 * Pille und Kachel) steht nach dem Node-Zweig und wird in
 * tests/decke-3d-viewer-browser.test.cjs per node:vm geprueft.
 *
 * Nur die Anordnungen round und linear-equal. Die versetzte Reihe (linear)
 * aus dem Prototyp erscheint nirgends, auch nicht als Clip.
 *
 * Der Browser-Name Moonou3D darf nie als DOM-id vorkommen (Browser legen fuer
 * jede id eine gleichnamige window-Eigenschaft an); der Test prueft das.
 */
(function (root) {
  'use strict';

  const ANORDNUNGEN = ['round', 'linear-equal'];
  const FARBEN = ['white', 'black'];

  function istAnordnung(wert) {
    return ANORDNUNGEN.indexOf(wert) !== -1;
  }

  /* Halterung aus der Variantenoption „Farbe". Was nicht eindeutig schwarz ist,
     wird weiss (Standardvariante). Der Optionsname wird zuerst exakt, dann ohne
     Gross/Klein gesucht. */
  function farbeAusVariante(optionen, farbOptionName) {
    const name = farbOptionName || 'Farbe';
    if (!optionen || typeof optionen !== 'object') return 'white';
    let wert = optionen[name];
    if (wert === undefined) {
      const klein = String(name).toLowerCase();
      for (const key of Object.keys(optionen)) {
        if (key.toLowerCase() === klein) { wert = optionen[key]; break; }
      }
    }
    const w = String(wert == null ? '' : wert).trim().toLowerCase();
    if (w === 'schwarz' || w === 'black') return 'black';
    return 'white';
  }

  /* Zustand fuer das ERSTE Oeffnen: Farbe aus der aktuell gewaehlten
     Variante (Review Focus 1), sonst Standard. Bei jedem weiteren Oeffnen
     bleiben Anordnung, Licht und Masse wie zuletzt (Review Focus 5); nur die
     Farbe wird per farbeAusVariante neu uebernommen. */
  function startZustand(eingabe) {
    const farbe = eingabe && FARBEN.indexOf(eingabe.farbe) !== -1 ? eingabe.farbe : 'white';
    return { anordnung: 'round', licht: true, farbe, masse: false };
  }

  /* Anordnungswechsel (Review Focus 2). Laeuft eine Animation, wird nur der
     letzte Wunsch gemerkt (wartend = ziel); der Aufrufer ruft nach dem Ende
     der Animation erneut auf, mit dem neuen aktuell und ziel = wartend.
     Ohne laufende Animation: Clip aktuell--ziel, bei gleichem Stand keiner.
     Unbekannte Ziele (z. B. die versetzte Reihe) aendern nichts. */
  function naechsterUebergang(eingabe) {
    const e = eingabe || {};
    const bisher = istAnordnung(e.wartend) ? e.wartend : null;
    if (!istAnordnung(e.ziel)) return { clip: null, wartend: e.laeuft ? bisher : null };
    if (e.laeuft) return { clip: null, wartend: e.ziel };
    if (!istAnordnung(e.aktuell) || e.aktuell === e.ziel) return { clip: null, wartend: null };
    return { clip: e.aktuell + '--' + e.ziel, wartend: null };
  }

  /* Eigenschaften fuer „3D Viewer Interaktion" (Spec 6). */
  function eventDaten(aktion, wert, produkt) {
    return { produkt, aktion, wert };
  }

  function istMass(n) {
    return typeof n === 'number' && Number.isFinite(n);
  }

  /* Masse und Anker einer Anordnung aus der Mass-Datei
     (assets/moonou-3d-<modell>.json, Task 2). Fehlt etwas oder ist ein Wert
     keine Zahl, kommt null: dann zeigt der Viewer keine Masse statt falscher. */
  function masseFuer(daten, anordnung) {
    if (!istAnordnung(anordnung) || !daten || !daten.presets) return null;
    const p = daten.presets[anordnung];
    if (!p || typeof p !== 'object') return null;
    if (!istMass(p.breite_mm) || !istMass(p.abhaenghoehe_mm) || !istMass(p.baldachin_mm)) return null;
    return {
      breite_mm: p.breite_mm,
      abhaenghoehe_mm: p.abhaenghoehe_mm,
      baldachin_mm: p.baldachin_mm,
      anker: p.anker && typeof p.anker === 'object' ? p.anker : {}
    };
  }


  /* Masse als deutsche Zentimeter, hoechstens eine Nachkommastelle
     (766,7 mm → „76,7 cm", 1729,8 mm → „173 cm"). Ohne Intl, damit Node
     und alte Browser dasselbe zeigen. */
  function cmText(mm) {
    const zehntel = Math.round(Number(mm));
    return String(zehntel / 10).replace('.', ',') + ' cm';
  }

  const MASS_LINIEN = [
    { id: 'breite', feld: 'breite_mm', text: (w) => 'Breite ' + w },
    { id: 'hoehe', feld: 'abhaenghoehe_mm', text: (w) => 'Abhänghöhe ' + w },
    { id: 'baldachin', feld: 'baldachin_mm', text: (w) => 'Ø ' + w }
  ];

  function istPunkt(p) {
    return Array.isArray(p) && p.length === 3 && p.every(istMass);
  }

  function istAnker(a) {
    return Array.isArray(a) && a.length === 2 && istPunkt(a[0]) && istPunkt(a[1]);
  }

  function linienAus(masse, vorlage) {
    if (!masse || !masse.anker) return [];
    return vorlage
      .filter((l) => istMass(masse[l.feld]) && istAnker(masse.anker[l.id]))
      .map((l) => ({ id: l.id, text: l.text(cmText(masse[l.feld])), a: masse.anker[l.id][0], b: masse.anker[l.id][1] }));
  }

  /* Linien fuer das Mass-Overlay: Text plus 3D-Anker a/b (Meter im
     Modellraum). Fehlt ein Anker, faellt nur diese Linie weg. */
  function massLinien(masse) {
    return linienAus(masse, MASS_LINIEN);
  }

  function kopie(masse) {
    return masse ? JSON.parse(JSON.stringify(masse)) : masse;
  }

  /* Masse waehrend des Anordnungswechsels: Werte und Anker gleiten von der
     einen Anordnung zur anderen (t schon geglaettet, 0..1). */
  function mischeMasse(von, nach, t) {
    if (!von || !nach) return kopie(nach || von || null);
    if (t <= 0) return kopie(von);
    if (t >= 1) return kopie(nach);
    const misch = (a, b) => a + (b - a) * t;
    const anker = {};
    for (const id of Object.keys(nach.anker || {})) {
      const va = von.anker && von.anker[id];
      const na = nach.anker[id];
      anker[id] = istAnker(va) && istAnker(na) ? na.map((p, i) => p.map((v, j) => misch(va[i][j], v))) : na;
    }
    return {
      breite_mm: misch(von.breite_mm, nach.breite_mm),
      abhaenghoehe_mm: misch(von.abhaenghoehe_mm, nach.abhaenghoehe_mm),
      baldachin_mm: misch(von.baldachin_mm, nach.baldachin_mm),
      anker
    };
  }

  /* Weicher Verlauf (smoothstep) wie im Prototyp, ausserhalb 0..1 geklemmt. */
  function glaetten(t) {
    const x = Math.min(1, Math.max(0, Number(t) || 0));
    return x * x * (3 - 2 * x);
  }

  /* Kamera-Abstand (Meter) fuer eine Buehne in CSS-Pixeln. Der Prototyp
     nutzt 4,1 m bei 32 Grad Blickfeld; mit Massen an lief die Breiten-Linie
     der runden Anordnung dort unten aus dem Bild, daher 4,4 m. Das reicht
     fuer quadratische und breite Buehnen. Bei hoher, schmaler Buehne (Handy hochkant) muss die
     breiteste Anordnung (In Reihe, ca. 1,12 m plus Labels) trotzdem ganz
     ins Bild: dann weiter weg. Model Viewer weitet das Blickfeld bei
     Hochformat zusaetzlich selbst; doppelt gerechnet wird das Set nur
     etwas kleiner, nie abgeschnitten. */
  const KAMERA = { basis: 4.4, halbesFovGrad: 16, halbeHoehe: 1.05, halbeBreite: 0.75 };

  function kameraAbstand(breitePx, hoehePx) {
    const b = Number(breitePx);
    const h = Number(hoehePx);
    if (!(b > 0) || !(h > 0)) return KAMERA.basis;
    const tan = Math.tan(KAMERA.halbesFovGrad * Math.PI / 180);
    const noetig = Math.max(KAMERA.basis, KAMERA.halbeHoehe / tan, KAMERA.halbeBreite / (tan * (b / h)));
    return Math.round(noetig * 100) / 100;
  }

  /* Blickwinkel wie im Prototyp: Rund schraeg (25 Grad), In Reihe frontal. */
  function kameraOrbit(anordnung, abstand) {
    return (anordnung === 'linear-equal' ? 0 : 25) + 'deg 80deg ' + abstand + 'm';
  }

  function hinweisText(feinerZeiger) {
    return feinerZeiger ? 'Ziehen zum Drehen · Mausrad zum Zoomen' : 'Ziehen zum Drehen · Zwei Finger zum Zoomen';
  }

  /* ---------- Tischlampen ------------------------------------------------
     Quelle: Tischlampen-Paket Tischlampen_V01 (viewer.js, models.json).
     Modell-Schluessel tisch-<name> (Boa, Finn, Japandi gibt es auch als
     Decke). Daten je Modell in assets/moonou-3d-tisch-<name>.json
     (tools/tisch3d/daten_export.py). */

  const ANSICHTEN = ['lampe', 'kabel'];
  const TISCH_PRAEFIX = 'tisch-';

  function produktTyp(modell) {
    return String(modell == null ? '' : modell).indexOf(TISCH_PRAEFIX) === 0 ? 'tisch' : 'decke';
  }

  /* „tisch-boa" → „Boa" (wie capitalize in Liquid) */
  function anzeigeName(modell) {
    const s = String(modell == null ? '' : modell);
    const kern = s.indexOf(TISCH_PRAEFIX) === 0 ? s.slice(TISCH_PRAEFIX.length) : s;
    return kern ? kern.charAt(0).toUpperCase() + kern.slice(1).toLowerCase() : '';
  }

  /* Wie Decke: Licht an. Keine Anordnung, keine Halterung. */
  function startZustandTisch() {
    return { licht: true, masse: false, ansicht: 'lampe' };
  }

  function tischMasse(daten) {
    const m = daten && daten.masse;
    if (!m || typeof m !== 'object') return null;
    if (!istMass(m.hoehe_mm) || !istMass(m.breite_mm) || !istMass(m.sockel_mm)) return null;
    return {
      hoehe_mm: m.hoehe_mm,
      breite_mm: m.breite_mm,
      sockel_mm: m.sockel_mm,
      anker: m.anker && typeof m.anker === 'object' ? m.anker : {}
    };
  }

  const MASS_LINIEN_TISCH = [
    { id: 'hoehe', feld: 'hoehe_mm', text: (w) => 'Höhe ' + w },
    { id: 'breite', feld: 'breite_mm', text: (w) => 'Breite ' + w },
    { id: 'sockel', feld: 'sockel_mm', text: (w) => 'Sockel Ø ' + w }
  ];

  function massLinienTisch(masse) {
    return linienAus(masse, MASS_LINIEN_TISCH);
  }

  function zahl4(n) {
    return String(Math.round(n * 10000) / 10000);
  }

  /* Kamera wie setView() im Paket: „Fokus Lampe" schraeg auf halber Hoehe,
     „Lampe & Kabel" von vorne oben, so weit weg, dass Dimmer und Lampe ins
     Bild passen. Ohne Daten oder Dimmer: Fokus Lampe mit 26 cm Hoehe. */
  const TISCH_KAMERA = { orbit: '25deg 78deg 0.8m', fov: '32deg', hoehe: 0.26, breite: 0.18 };

  function kameraTisch(daten, ansicht) {
    const g = daten && daten.groesse_m;
    const gOk = Array.isArray(g) && g.length === 3 && g.every((v) => istMass(v) && v > 0);
    const breite = gOk ? g[0] : TISCH_KAMERA.breite;
    const hoehe = gOk ? g[1] : TISCH_KAMERA.hoehe;
    const d = daten && daten.dimmer;
    if (ansicht === 'kabel' && gOk && d && istPunkt(d.mitte_m) && istPunkt(d.gehaeuse_m)) {
      const spanne = d.mitte_m[0] + d.gehaeuse_m[0] / 2 + 0.16 + breite / 2;
      return {
        orbit: '0deg 55deg ' + zahl4(Math.max(1.22, spanne * 2.05)) + 'm',
        ziel: zahl4(d.mitte_m[0] * 0.48) + 'm ' + zahl4(hoehe * 0.42) + 'm -0.035m',
        fov: TISCH_KAMERA.fov
      };
    }
    return { orbit: TISCH_KAMERA.orbit, ziel: '0m ' + zahl4(hoehe / 2) + 'm 0m', fov: TISCH_KAMERA.fov };
  }

  /* Warmes Licht wie warmLight im Paket (an 2100 K angelehnt, an Fotos von
     Numo/Twuro abgestimmt; Vorschau, keine Photometrie). Aus: helles PLA bzw.
     Opalglas ohne Emission. */
  const TISCH_LICHT = {
    emissiv: [1, 0.245, 0.008],
    aus: [0, 0, 0],
    staerke: 1.5,
    schirm: { an: [0.22, 0.065, 0.004, 1], aus: [0.72, 0.69, 0.63, 1] },
    birne: { an: [1, 0.267, 0.012, 1], aus: [0.82, 0.8, 0.74, 1] }
  };

  /* Eichenholzsockel: Faktor auf die Furnier-Textur der GLBs, damit das Holz
     dunkler und waermer wirkt wie der echte Sockel (Till, 11.10.). Gilt bei
     Licht an und aus. */
  const TISCH_HOLZ = [0.66, 0.52, 0.38, 1];

  function holzFarbeTisch(name) {
    return name === 'Wood_Oak' ? TISCH_HOLZ.slice() : null;
  }

  function lichtMaterialTisch(name, an) {
    let teil = null;
    if (name === 'Lamp_Shade' || name === 'Shade_Mount') teil = TISCH_LICHT.schirm;
    else if (name === 'E27_Opal_Bulb') teil = TISCH_LICHT.birne;
    if (!teil) return null;
    return {
      basis: (an ? teil.an : teil.aus).slice(),
      emissiv: (an ? TISCH_LICHT.emissiv : TISCH_LICHT.aus).slice(),
      staerke: an ? TISCH_LICHT.staerke : 0
    };
  }

  const api = {
    ANORDNUNGEN, farbeAusVariante, startZustand, naechsterUebergang, eventDaten, masseFuer,
    cmText, massLinien, mischeMasse, glaetten, kameraAbstand, kameraOrbit, hinweisText,
    ANSICHTEN, produktTyp, anzeigeName, startZustandTisch, tischMasse, massLinienTisch,
    kameraTisch, lichtMaterialTisch, holzFarbeTisch
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  root.Moonou3D = api;

  /* ====================================================================
     Teil 2: Browser (Plan Task 4)

     Markup-Vertrag (Snippet snippets/moonou-3d-viewer.liquid, Task 5):
       Wurzel [data-moonou-3d] mit data-glb, data-masse, data-produkt,
         data-runtime, data-poster, data-kachel-position
       Oeffner [data-moonou-3d-open] mit data-einstieg="pille|kachel"
         (Pille als Button in der Wurzel, Kachel in
         <template data-moonou-3d-kachel>)
       <dialog data-moonou-3d-dialog> mit den Kind-Selektoren unten
         (vollstaendige Liste im Bericht task-4-bericht.md).

     Vor dem ersten Oeffnen laedt nichts: kein Runtime-Script, keine GLB,
     keine Mass-Datei, kein Poster. Die Runtime (Model Viewer 4.3.1) wird
     genau einmal als <script type="module"> eingefuegt, und gar nicht,
     wenn model-viewer schon definiert ist (theme.js laedt bei Shopify-3D-
     Medien Shopify.loadFeatures('model-viewer-ui'), das dasselbe Element
     definiert). Tracking nur ueber den vorhandenen Mixpanel-Stub, Mixpanel
     wird nie vorgezogen.
     ==================================================================== */

  const doc = root.document;
  if (!doc) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const CLIP_SEKUNDEN = 0.8;
  const CLIP_MS = 800;
  const RUNTIME_ZEITLIMIT_MS = 45000;
  const FARBNAME = { white: 'Weiß', black: 'Schwarz' };
  const BLOOM_AUSWAHL = '["Boa_Shade_0","Boa_Shade_1","Boa_Shade_2","E27_Bulb_0","E27_Bulb_1","E27_Bulb_2"]';
  const KAMERA_ZIEL = '0m 0.48m 0m';
  /* Belichtung: Licht an wie im Prototyp (1). Auf der hellen Buehne bei
     Licht aus etwas weniger, damit weisse Kabel/Halterung und die Rippen der
     Schirme nicht im Beige verschwinden (Task 7, Sichtpruefung 0,7/0,85/1). */
  const BELICHTUNG = { an: '1', aus: '0.85' };
  const STEUERUNG = '[data-moonou-3d-anordnung], [data-moonou-3d-schalter], [data-moonou-3d-farbe], [data-moonou-3d-ansicht]';
  /* Tischlampen (data-typ="tisch"): Bloom und Schatten wie index.html im
     Tischlampen-Paket; Knotennamen der Tisch-GLBs */
  const TISCH_BLOOM_AUSWAHL = '["Lamp_Shade","Shade_Mount","E27_Bulb"]';
  const TISCH_BELICHTUNG = { an: '1', aus: '0.85' };

  let wurzel = null;
  let dialog = null;
  let bereich = doc;
  let pille = null;
  let galerieBeobachter = null;
  let beobachteteGalerie = null;
  let viewer = null;
  let status = 'leer';
  let zustand = null;
  let laeuft = false;
  let wartend = null;
  let zielLaufend = null;
  let massVersprechen = null;
  let massDaten = null;
  let aktuelleMasse = null;
  let ausloeser = null;
  let offen = false;
  let downAufHintergrund = false;
  let zeichnenFrame = 0;
  let kachelTimer = null;
  let kachelVersuche = 0;
  let runtimeVersprechen = null;
  let runtimeVersuche = 0;
  let ladeStand = 0;
  let typ = 'decke';
  let ladeVersuche = 0;

  function istTisch() { return typ === 'tisch'; }
  function belichtung() { return istTisch() ? TISCH_BELICHTUNG : BELICHTUNG; }

  function im(sel) { return dialog ? dialog.querySelector(sel) : null; }
  function alle(sel) { return dialog ? Array.prototype.slice.call(dialog.querySelectorAll(sel)) : []; }
  function raf(fn) { return root.requestAnimationFrame ? root.requestAnimationFrame(fn) : root.setTimeout(() => fn(Date.now()), 16); }
  function cafAbbrechen(id) { if (root.cancelAnimationFrame) root.cancelAnimationFrame(id); else root.clearTimeout(id); }
  function medien(q) { try { return !!(root.matchMedia && root.matchMedia(q).matches); } catch (e) { return false; } }
  function reduziert() { return medien('(prefers-reduced-motion: reduce)'); }
  function produkt() { return wurzel ? wurzel.getAttribute('data-produkt') || '' : ''; }

  /* Mixpanel laedt bei der ersten Interaktion ueber einen window-click-
     Listener (snippets/moonou-tracking-head.liquid), der NACH dieser
     document-Delegation laeuft. Ist der Pillen-Klick die erste Interaktion,
     fehlt window.mixpanel genau jetzt: dann einmal nach dem Klick erneut
     versuchen. Mixpanel wird dabei nie vorgezogen. */
  function track(name, eigenschaften) {
    const senden = () => {
      try {
        if (root.mixpanel && typeof root.mixpanel.track === 'function') {
          root.mixpanel.track(name, eigenschaften);
          return true;
        }
      } catch (e) { return true; /* Tracking darf den Viewer nie stoeren */ }
      return false;
    };
    if (!senden()) root.setTimeout(senden, 0);
  }

  function zuruecksetzen() {
    if (galerieBeobachter) galerieBeobachter.disconnect();
    if (kachelTimer) root.clearTimeout(kachelTimer);
    wurzel = dialog = pille = null;
    bereich = doc;
    galerieBeobachter = beobachteteGalerie = null;
    viewer = null;
    status = 'leer';
    zustand = null;
    laeuft = false;
    wartend = zielLaufend = null;
    massVersprechen = massDaten = aktuelleMasse = null;
    ausloeser = null;
    offen = false;
    kachelTimer = null;
    kachelVersuche = 0;
    typ = 'decke';
    ladeVersuche = 0;
  }

  function init() {
    if (wurzel && wurzel.isConnected) return true;
    zuruecksetzen();
    let gefunden = null;
    Array.prototype.forEach.call(doc.querySelectorAll('[data-moonou-3d]'), (k) => {
      if (!gefunden && String(k.getAttribute('data-glb') || '').trim()) gefunden = k;
    });
    if (!gefunden) return false;
    const d = gefunden.querySelector('[data-moonou-3d-dialog]');
    if (!d) return false;
    wurzel = gefunden;
    dialog = d;
    typ = gefunden.getAttribute('data-typ') === 'tisch' || produktTyp(gefunden.getAttribute('data-produkt')) === 'tisch' ? 'tisch' : 'decke';
    bereich = wurzel.closest('.moonou-product') || doc;
    pille = wurzel.querySelector('[data-moonou-3d-open][data-einstieg="pille"]');
    dialog.addEventListener('pointerdown', (ev) => { downAufHintergrund = ev.target === dialog; });
    dialog.addEventListener('click', beiDialogKlick);
    dialog.addEventListener('close', aufraeumen);
    dialog.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') { ev.preventDefault(); schliessen(); }
    });
    return true;
  }

  /* ---------- Variante → Farbe der Halterung ------------------------------ */

  function jsonAus(sel) {
    const el = bereich.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Quelle der aktuellen Variante: input[name="id"] im Produktformular
     (theme.js ProductInfo.updateVariantInputs setzt es bei jedem Wechsel),
     dazu die Varianten aus [data-variants] im variant-picker. Fallback:
     [data-selected-variant] (wird beim Wechsel mit dem Picker neu gerendert). */
  function aktuelleVariante() {
    const input = bereich.querySelector('form[is="product-form"] input[name="id"]') || bereich.querySelector('input[name="id"]');
    const id = input ? String(input.value || '') : '';
    const liste = jsonAus('[data-variants]');
    if (id && Array.isArray(liste)) {
      for (const v of liste) if (v && String(v.id) === id) return v;
    }
    return jsonAus('[data-selected-variant]');
  }

  /* Optionen als {farbe: 'Schwarz', leuchte: '...'}: Namen aus den
     Fieldsets des Pickers (data-option-slug + data-option-index). */
  function aktuelleOptionen() {
    const v = aktuelleVariante();
    if (!v) return {};
    const optionen = {};
    Array.prototype.forEach.call(bereich.querySelectorAll('variant-picker [data-option-slug]'), (f) => {
      const index = String(f.getAttribute('data-option-index') || '').replace(/\D/g, '');
      const slug = f.getAttribute('data-option-slug');
      if (index && slug) optionen[slug] = v['option' + index];
    });
    if (optionen.farbe === undefined) {
      const werte = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3];
      optionen.farbe = werte.find((w) => farbeAusVariante({ farbe: w }, 'farbe') === 'black') || '';
    }
    return optionen;
  }

  /* ---------- Oeffnen / Schliessen ---------------------------------------- */

  function oeffnen(einstieg, ausloesendesElement) {
    if (!init()) return false;
    ausloeser = ausloesendesElement || doc.activeElement || null;
    if (istTisch()) {
      /* Tischlampe: keine Halterung; Licht, Masse, Ansicht bleiben wie zuletzt */
      if (!zustand) zustand = startZustandTisch();
    } else {
      const farbe = farbeAusVariante(aktuelleOptionen(), 'Farbe');
      /* startZustand nur beim ersten Oeffnen; danach bleiben Anordnung, Licht
         und Masse, nur die Farbe folgt der Variante (Review Focus 5). */
      if (!zustand) zustand = startZustand({ farbe });
      else zustand.farbe = farbe;
    }

    hinweiseSetzen();
    zeigeZustand();
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch (e) {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-ohne-modal');
      }
    }
    offen = true;
    doc.documentElement.classList.add('moonou-3d-offen');
    const schliessenKnopf = im('[data-moonou-3d-schliessen]');
    if (schliessenKnopf && schliessenKnopf.focus) schliessenKnopf.focus();

    track('3D Viewer geöffnet', { produkt: produkt(), einstieg: einstieg || 'pille' });

    if (status === 'leer' || status === 'fehler') laden();
    else if (status === 'bereit') {
      anwenden();
      masseZeichnenPlanen();
    }
    return true;
  }

  function schliessen() {
    if (!dialog) return;
    if (dialog.open && typeof dialog.close === 'function' && !dialog.classList.contains('is-ohne-modal')) dialog.close();
    else dialog.removeAttribute('open');
    aufraeumen();
  }

  function aufraeumen() {
    if (!offen) return;
    offen = false;
    doc.documentElement.classList.remove('moonou-3d-offen');
    const ziel = ausloeser && ausloeser.isConnected ? ausloeser : (pille && pille.isConnected ? pille : null);
    ausloeser = null;
    if (ziel && ziel.focus) {
      try { ziel.focus({ preventScroll: true }); } catch (e) { ziel.focus(); }
    }
  }

  function beiDialogKlick(ev) {
    const t = ev.target;
    if (t === dialog) {
      const vomHintergrund = downAufHintergrund;
      downAufHintergrund = false;
      /* Nur echte Hintergrund-Klicks: wer im Modell dreht und ausserhalb
         loslaesst, loest auch einen click auf dem dialog aus. */
      if (vomHintergrund) schliessen();
      return;
    }
    downAufHintergrund = false;
    if (!t || !t.closest) return;
    if (t.closest('[data-moonou-3d-schliessen]')) { schliessen(); return; }
    const knopf = t.closest(STEUERUNG);
    if (!knopf || knopf.disabled || status !== 'bereit') return;

    const ziel = knopf.getAttribute('data-moonou-3d-anordnung');
    if (ziel) {
      if (ANORDNUNGEN.indexOf(ziel) === -1) return;
      if (ziel !== wunschAnordnung()) track('3D Viewer Interaktion', eventDaten('anordnung', ziel, produkt()));
      wechsleAnordnung(ziel);
      return;
    }
    const ansicht = knopf.getAttribute('data-moonou-3d-ansicht');
    if (ansicht) {
      if (!istTisch() || ANSICHTEN.indexOf(ansicht) === -1 || ansicht === zustand.ansicht) return;
      zustand.ansicht = ansicht;
      zeigeZustand();
      kameraSetzen(true);
      track('3D Viewer Interaktion', eventDaten('ansicht', ansicht, produkt()));
      return;
    }
    const farbe = knopf.getAttribute('data-moonou-3d-farbe');
    if (farbe) {
      if (FARBEN.indexOf(farbe) === -1 || farbe === zustand.farbe) return;
      zustand.farbe = farbe;
      anwenden();
      zeigeZustand();
      track('3D Viewer Interaktion', eventDaten('farbe', farbe, produkt()));
      return;
    }
    const schalter = knopf.getAttribute('data-moonou-3d-schalter');
    if (schalter === 'licht') {
      zustand.licht = !zustand.licht;
      anwenden();
      zeigeZustand();
      track('3D Viewer Interaktion', eventDaten('licht', zustand.licht ? 'an' : 'aus', produkt()));
    } else if (schalter === 'masse') {
      zustand.masse = !zustand.masse;
      zeigeZustand();
      masseZeichnenPlanen();
      if (zustand.masse) masseLaden().then(masseKonfigurieren);
      track('3D Viewer Interaktion', eventDaten('masse', zustand.masse ? 'an' : 'aus', produkt()));
    }
  }

  /* ---------- Anzeige des Zustands ---------------------------------------- */

  function wunschAnordnung() {
    return wartend || zielLaufend || (zustand && zustand.anordnung) || 'round';
  }

  function hinweiseSetzen() {
    const text = hinweisText(medien('(hover: hover) and (pointer: fine)'));
    alle('[data-moonou-3d-hinweis]').forEach((el) => { el.textContent = text; });
  }

  function zeigeZustand() {
    if (!zustand) return;
    const anordnung = wunschAnordnung();
    alle('[data-moonou-3d-anordnung]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-moonou-3d-anordnung') === anordnung));
    });
    alle('[data-moonou-3d-farbe]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-moonou-3d-farbe') === zustand.farbe));
    });
    alle('[data-moonou-3d-ansicht]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-moonou-3d-ansicht') === zustand.ansicht));
    });
    alle('[data-moonou-3d-schalter]').forEach((b) => {
      const an = b.getAttribute('data-moonou-3d-schalter') === 'licht' ? zustand.licht : zustand.masse;
      b.setAttribute('aria-checked', String(!!an));
    });
    alle('[data-moonou-3d-farbname]').forEach((el) => { el.textContent = FARBNAME[zustand.farbe]; });
    alle('[data-moonou-3d-masshinweis]').forEach((el) => { el.hidden = !zustand.masse; });
    dialog.setAttribute('data-masse', zustand.masse ? 'an' : 'aus');
    /* Buehnenfarbe folgt dem Licht schon beim Oeffnen, auch waehrend des
       Ladens und im Fehlerzustand (CSS: hell bei Licht aus, dunkel bei an) */
    const buehne = im('[data-moonou-3d-buehne]');
    if (buehne) buehne.classList.toggle('is-licht', !!zustand.licht);
  }

  function setzeStatus(neu) {
    status = neu;
    dialog.setAttribute('data-zustand', neu);
    const laden = im('[data-moonou-3d-laden]');
    const fehlerBox = im('[data-moonou-3d-fehler]');
    const poster = im('[data-moonou-3d-poster]');
    if (laden) laden.hidden = neu !== 'laden';
    if (fehlerBox) fehlerBox.hidden = neu !== 'fehler';
    if (poster) poster.hidden = neu !== 'laden' || !poster.getAttribute('src');
    alle(STEUERUNG).forEach((b) => { b.disabled = neu !== 'bereit'; });
    masseZeichnenPlanen();
  }

  function fortschritt(prozent) {
    const p = Math.max(0, Math.min(100, Math.round(prozent)));
    const text = im('[data-moonou-3d-prozent]');
    const fuellung = im('[data-moonou-3d-fortschritt]');
    const balken = im('[data-moonou-3d-balken]');
    if (text) text.textContent = p + ' %';
    if (fuellung) fuellung.style.width = p + '%';
    if (balken) balken.setAttribute('aria-valuenow', String(p));
  }

  /* ---------- Laden ------------------------------------------------------- */

  function webglOk() {
    try {
      const c = doc.createElement('canvas');
      return !!(c && c.getContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) {
      return false;
    }
  }

  function ladeRuntime(url) {
    const ce = root.customElements;
    if (ce && ce.get('model-viewer')) return Promise.resolve();
    if (runtimeVersprechen) return runtimeVersprechen;
    if (!ce || !url) return Promise.reject(new Error('Runtime fehlt'));
    runtimeVersuche++;
    runtimeVersprechen = new Promise((resolve, reject) => {
      let fertig = false;
      let timer = null;
      const ende = (fehlerGrund) => {
        if (fertig) return;
        fertig = true;
        root.clearTimeout(timer);
        if (fehlerGrund) {
          runtimeVersprechen = null;
          reject(fehlerGrund);
        } else resolve();
      };
      timer = root.setTimeout(() => ende(new Error('Runtime Zeitlimit')), RUNTIME_ZEITLIMIT_MS);
      const s = doc.createElement('script');
      s.type = 'module';
      /* Ein fehlgeschlagener Modul-Abruf bleibt im Browser gemerkt: beim
         erneuten Versuch eine andere URL, sonst scheitert er sofort wieder. */
      s.src = runtimeVersuche > 1 ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'versuch=' + runtimeVersuche : url;
      s.setAttribute('data-moonou-3d-runtime', '');
      s.onload = () => {
        if (ce.get('model-viewer')) ende();
        else ce.whenDefined('model-viewer').then(() => ende());
      };
      s.onerror = () => {
        if (s.remove) s.remove();
        ende(new Error('Runtime nicht geladen'));
      };
      doc.head.appendChild(s);
    });
    return runtimeVersprechen;
  }

  function masseLaden() {
    if (massVersprechen) return massVersprechen;
    const url = wurzel && wurzel.getAttribute('data-masse');
    if (!url || typeof root.fetch !== 'function') return Promise.resolve(null);
    massVersprechen = root.fetch(url, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error('Mass-Datei');
        return r.json();
      })
      .then((daten) => {
        massDaten = daten;
        return daten;
      }, () => {
        massVersprechen = null;
        return null;
      });
    return massVersprechen;
  }

  function laden() {
    ladeStand = 0;
    ladeVersuche++;
    fortschritt(0);
    const poster = im('[data-moonou-3d-poster]');
    const posterUrl = wurzel.getAttribute('data-poster');
    if (poster && posterUrl && poster.getAttribute('src') !== posterUrl) poster.setAttribute('src', posterUrl);
    setzeStatus('laden');
    if (!webglOk()) {
      fehler();
      return;
    }
    const daten = masseLaden();
    const runtime = ladeRuntime(wurzel.getAttribute('data-runtime'));
    /* Tisch: Kamera und Name kommen aus der Daten-Datei, also auf beides
       warten (masseLaden scheitert nie, liefert dann null) */
    (istTisch() ? Promise.all([runtime, daten]) : runtime).then(() => {
      if (status === 'laden' && !viewer) viewerErzeugen();
    }, fehler);
  }

  /* Tisch-GLBs sind meshopt-komprimiert. Model Viewer laedt den Decoder
     (klassisches Script) beim Setzen der statischen Eigenschaft, darum vor
     dem ersten src. Eine gescheiterte URL merkt er sich: nach einem Fehler
     eine neue URL. */
  function meshoptSetzen() {
    const url = wurzel.getAttribute('data-meshopt');
    const klasse = root.customElements && root.customElements.get('model-viewer');
    if (!url || !klasse) return;
    const ziel = ladeVersuche > 1 ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'versuch=' + ladeVersuche : url;
    try {
      if (klasse.meshoptDecoderLocation !== ziel) klasse.meshoptDecoderLocation = ziel;
    } catch (e) { /* aelteres model-viewer ohne meshopt: GLB-Fehler zeigt dann die Meldung */ }
  }

  function tischName() {
    return (massDaten && typeof massDaten.name === 'string' && massDaten.name) || anzeigeName(produkt());
  }

  function element(tag, attribute) {
    const el = doc.createElement(tag);
    Object.keys(attribute || {}).forEach((k) => el.setAttribute(k, attribute[k]));
    return el;
  }

  function viewerErzeugen() {
    const buehne = im('[data-moonou-3d-buehne]');
    if (!buehne) { fehler(); return; }
    if (istTisch()) { tischViewerErzeugen(buehne); return; }
    const mv = element('model-viewer', {
      class: 'moonou-3d-viewer',
      alt: '3D-Modell der Deckenlampe mit drei Pendelleuchten und Baldachin',
      'animation-crossfade-duration': '0',
      'camera-controls': '',
      'touch-action': 'none',
      'interaction-prompt': 'none',
      'camera-orbit': kameraOrbit(zustand.anordnung, KAMERA.basis),
      'camera-target': KAMERA_ZIEL,
      'min-camera-orbit': 'auto 8deg auto',
      'max-camera-orbit': 'auto 172deg 10m',
      'field-of-view': '32deg',
      exposure: zustand.licht ? BELICHTUNG.an : BELICHTUNG.aus,
      'environment-image': 'neutral',
      'shadow-intensity': '0'
    });
    const composer = element('effect-composer', { 'render-mode': 'quality', msaa: '2' });
    composer.appendChild(element('selective-bloom-effect', {
      selection: BLOOM_AUSWAHL, strength: '0.22', radius: '0.65', threshold: '0.4', smoothing: '0.2',
      'blend-mode': zustand.licht ? 'ADD' : 'SKIP'
    }));
    composer.appendChild(element('color-grade-effect', { tonemapping: 'ACES_FILMIC' }));
    mv.appendChild(composer);
    viewerEinsetzen(mv, buehne);
  }

  function viewerEinsetzen(mv, buehne) {
    /* Eigener Fortschrittsbalken im Dialog, den von Model Viewer leeren */
    mv.appendChild(element('div', { slot: 'progress-bar' }));
    mv.addEventListener('progress', beiFortschritt);
    mv.addEventListener('load', beiGeladen);
    mv.addEventListener('error', beiFehler);
    mv.addEventListener('camera-change', masseZeichnenPlanen);
    if (typeof root.ResizeObserver === 'function') {
      try { new root.ResizeObserver(masseZeichnenPlanen).observe(mv); } catch (e) { /* ohne ResizeObserver */ }
    }
    viewer = mv;
    buehne.appendChild(mv);
    mv.setAttribute('src', wurzel.getAttribute('data-glb'));
  }

  /* Tischlampe: Attribute wie index.html im Tischlampen-Paket (Kamera aus
     setView, Schatten, Bloom-Auswahl), Buehne und Belichtung wie Decke. */
  function tischViewerErzeugen(buehne) {
    const kamera = kameraTisch(massDaten, zustand.ansicht);
    const mv = element('model-viewer', {
      class: 'moonou-3d-viewer',
      alt: '3D-Modell der Tischlampe ' + tischName() + ' mit Eichenholzsockel',
      'camera-controls': '',
      'touch-action': 'none',
      'interaction-prompt': 'none',
      'camera-orbit': kamera.orbit,
      'camera-target': kamera.ziel,
      'min-camera-orbit': 'auto 5deg 0.28m',
      'max-camera-orbit': 'auto 170deg 2m',
      'field-of-view': kamera.fov,
      exposure: zustand.licht ? TISCH_BELICHTUNG.an : TISCH_BELICHTUNG.aus,
      'environment-image': wurzel.getAttribute('data-umgebung') || 'neutral',
      'shadow-intensity': '0.6',
      'shadow-softness': '1'
    });
    tischEffekte(mv, !!zustand.licht);
    meshoptSetzen();
    viewerEinsetzen(mv, buehne);
  }

  /* Effekt-Ebene nur bei Licht an (Bloom). Bei Licht aus verrechnet der
     Composer halbtransparente Pixel falsch mit der hellen Buehne: das
     ausblendende Kabelende (Cable_Fade) blitzte weiss auf und brach hart ab,
     der Bodenschatten fehlte (Till 12.10.). Licht aus daher ohne Composer,
     dasselbe ACES-Tonemapping direkt im Model Viewer. */
  function tischEffekte(mv, an) {
    const vorhanden = mv.querySelector('effect-composer');
    if (!an) {
      if (vorhanden) vorhanden.remove();
      mv.setAttribute('tone-mapping', 'aces');
      return;
    }
    mv.removeAttribute('tone-mapping');
    if (vorhanden) return;
    const composer = element('effect-composer', { 'render-mode': 'quality', msaa: '2' });
    composer.appendChild(element('selective-bloom-effect', {
      selection: TISCH_BLOOM_AUSWAHL, strength: '0.52', radius: '0.88', threshold: '0.2', smoothing: '0.32',
      'blend-mode': 'ADD'
    }));
    composer.appendChild(element('color-grade-effect', { tonemapping: 'ACES_FILMIC' }));
    mv.appendChild(composer);
  }

  /* Model Viewer meldet vor der GLB eine eigene Runde 0 → 1 und beginnt
     dann wieder bei 0. Der Balken laeuft darum nur vorwaerts, 100 % setzt
     erst das load-Ereignis (beiGeladen). */
  function beiFortschritt(ev) {
    if (ev.currentTarget !== viewer || status !== 'laden') return;
    const p = ev.detail && Number(ev.detail.totalProgress);
    if (!Number.isFinite(p) || p >= 1) return;
    const prozent = Math.round(p * 100);
    if (prozent <= ladeStand) return;
    ladeStand = prozent;
    fortschritt(prozent);
  }

  function beiGeladen(ev) {
    if (ev.currentTarget !== viewer) return;
    const mv = viewer;
    anwenden();
    kameraSetzen();
    if (!istTisch() && zustand.anordnung !== 'round') endposeSetzen(mv, 'round--' + zustand.anordnung);
    fortschritt(100);
    setzeStatus('bereit');
    zeigeZustand();
    masseLaden().then(masseKonfigurieren);
  }

  function beiFehler(ev) {
    if (ev.currentTarget !== viewer) return;
    fehler();
  }

  function fehler() {
    if (viewer) {
      if (viewer.remove) viewer.remove();
      viewer = null;
    }
    laeuft = false;
    wartend = zielLaufend = null;
    if (dialog) setzeStatus('fehler');
  }

  function kameraSetzen(weich) {
    if (!viewer) return;
    if (istTisch()) { tischKameraSetzen(weich); return; }
    const abstand = kameraAbstand(viewer.clientWidth, viewer.clientHeight);
    viewer.setAttribute('camera-orbit', kameraOrbit(zustand.anordnung, abstand));
    viewer.setAttribute('camera-target', KAMERA_ZIEL);
    viewer.setAttribute('max-camera-orbit', 'auto 172deg ' + Math.max(10, Math.ceil(abstand * 2)) + 'm');
    if (typeof viewer.jumpCameraToGoal === 'function') viewer.jumpCameraToGoal();
  }

  /* Wie setView() im Paket: Ziel setzen und Model Viewer zum Neuberechnen
     zwingen (hat die Kundin gedreht, ist der Attributwert gleich und Lit
     wuerde sonst nichts tun). Weich = Kamerafahrt, sonst Sprung (Laden,
     reduzierte Bewegung). */
  function tischKameraSetzen(weich) {
    const mv = viewer;
    const k = kameraTisch(massDaten, zustand.ansicht);
    mv.setAttribute('camera-orbit', k.orbit);
    mv.setAttribute('camera-target', k.ziel);
    mv.setAttribute('field-of-view', k.fov);
    if (typeof mv.requestUpdate === 'function') ['cameraOrbit', 'cameraTarget', 'fieldOfView'].forEach((p) => mv.requestUpdate(p));
    if (weich && !reduziert()) return;
    if (!weich) {
      if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
      return;
    }
    Promise.resolve(mv.updateComplete).then(() => {
      if (mv === viewer && typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
    });
  }

  /* Neuer Viewer nach einem Fehler, Zustand war schon In Reihe: Clip auf
     sein Ende stellen statt abzuspielen. */
  function endposeSetzen(mv, clip) {
    mv.animationName = clip;
    Promise.resolve(mv.updateComplete).then(() => {
      if (mv !== viewer) return;
      mv.play({ repetitions: 1 });
      mv.pause();
      mv.currentTime = CLIP_SEKUNDEN;
    });
  }

  /* ---------- Licht und Halterung (Materialnamen wie im Prototyp) -------- */

  function anwenden() {
    if (!zustand) return;
    const buehne = im('[data-moonou-3d-buehne]');
    if (buehne) buehne.classList.toggle('is-licht', !!zustand.licht);
    if (!viewer) return;
    viewer.setAttribute('exposure', zustand.licht ? belichtung().an : belichtung().aus);
    const bloom = viewer.querySelector('selective-bloom-effect');
    if (bloom) bloom.setAttribute('blend-mode', zustand.licht ? 'ADD' : 'SKIP');
    const model = viewer.model;
    if (!model || !model.materials) return;
    const an = !!zustand.licht;
    if (istTisch()) {
      tischEffekte(viewer, an);
      Array.prototype.forEach.call(model.materials, (material) => {
        const holz = holzFarbeTisch(material.name);
        if (holz) material.pbrMetallicRoughness.setBaseColorFactor(holz);
        const wert = lichtMaterialTisch(material.name, an);
        if (!wert) return;
        material.pbrMetallicRoughness.setBaseColorFactor(wert.basis);
        material.setEmissiveFactor(wert.emissiv);
        material.setEmissiveStrength(wert.staerke);
      });
      return;
    }
    const schwarz = zustand.farbe === 'black';
    Array.prototype.forEach.call(model.materials, (material) => {
      const name = material.name;
      if (name === 'Boa_Shade') {
        /* Sichtbarer An/Aus-Zustand, keine Raumlicht-Berechnung */
        material.pbrMetallicRoughness.setBaseColorFactor(an ? [0.60, 0.48, 0.26, 1] : [0.72, 0.69, 0.63, 1]);
        material.setEmissiveFactor(an ? [1, 0.66, 0.25] : [0, 0, 0]);
        material.setEmissiveStrength(an ? 0.55 : 0);
      } else if (name === 'E27_Opal_Bulb') {
        material.pbrMetallicRoughness.setBaseColorFactor(an ? [1, 0.84, 0.54, 1] : [0.82, 0.80, 0.74, 1]);
        material.setEmissiveFactor(an ? [1, 0.74, 0.38] : [0, 0, 0]);
        material.setEmissiveStrength(an ? 1.1 : 0);
      } else if (name === 'Hardware_White' || name === 'Textile_Cable') {
        material.pbrMetallicRoughness.setBaseColorFactor(schwarz ? [0.026, 0.024, 0.022, 1] : [0.86, 0.84, 0.80, 1]);
      }
    });
  }

  /* ---------- Anordnung (glTF-Clips, 800 ms) ------------------------------ */

  function wechsleAnordnung(ziel) {
    const r = naechsterUebergang({ aktuell: zustand.anordnung, ziel, laeuft, wartend });
    wartend = r.wartend;
    if (r.clip) spieleClip(r.clip, zustand.anordnung, ziel);
    zeigeZustand();
  }

  function spieleClip(clip, von, nach) {
    const mv = viewer;
    if (!mv) return;
    laeuft = true;
    zielLaufend = nach;
    const vonMasse = massDaten ? masseFuer(massDaten, von) : null;
    const nachMasse = massDaten ? masseFuer(massDaten, nach) : null;
    mv.animationName = clip;
    Promise.resolve(mv.updateComplete)
      .then(() => {
        /* Wie im Prototyp: Clip anwerfen, anhalten und die Zeit selbst
           fuehren, damit die Masslinien im selben Takt gleiten. */
        mv.play({ repetitions: 1 });
        mv.pause();
        mv.currentTime = 0;
        const dauer = reduziert() ? 0 : CLIP_MS;
        return new Promise((resolve) => {
          let start = null;
          const bild = (jetzt) => {
            if (mv !== viewer) { resolve(); return; }
            if (start === null) start = jetzt;
            const t = dauer ? Math.min((jetzt - start) / dauer, 1) : 1;
            mv.currentTime = t * CLIP_SEKUNDEN;
            if (vonMasse && nachMasse) {
              aktuelleMasse = mischeMasse(vonMasse, nachMasse, glaetten(t));
              hotspotsNachfuehren();
              masseZeichnenPlanen();
            }
            if (t < 1) raf(bild);
            else resolve();
          };
          if (dauer) raf(bild);
          else bild(0);
        });
      })
      .catch(() => { /* Endstand trotzdem uebernehmen */ })
      .then(() => {
        laeuft = false;
        zielLaufend = null;
        if (mv !== viewer) return;
        zustand.anordnung = nach;
        masseKonfigurieren();
        if (wartend) {
          const naechstes = wartend;
          wartend = null;
          wechsleAnordnung(naechstes);
        } else zeigeZustand();
      });
  }

  /* ---------- Masse (SVG-Overlay ueber projizierten 3D-Ankern) ----------- */

  function svgElement(tag, attribute) {
    const el = doc.createElementNS(SVG_NS, tag);
    Object.keys(attribute || {}).forEach((k) => el.setAttribute(k, attribute[k]));
    return el;
  }

  function position(punkt) {
    return punkt.map((v) => v + 'm').join(' ');
  }

  /* Linien je Produkttyp (Decke: Anordnung, Tisch: Hoehe/Breite/Sockel) */
  function linien(masse) {
    return istTisch() ? massLinienTisch(masse) : massLinien(masse);
  }

  function masseKonfigurieren() {
    if (!viewer || !massDaten) { masseZeichnenPlanen(); return; }
    if (!laeuft) aktuelleMasse = istTisch() ? tischMasse(massDaten) : masseFuer(massDaten, zustand.anordnung);
    const linien = istTisch() ? massLinienTisch(aktuelleMasse) : massLinien(aktuelleMasse);
    Array.prototype.forEach.call(viewer.querySelectorAll('[data-moonou-3d-anker]'), (n) => n.remove());
    linien.forEach((l) => {
      ['a', 'b'].forEach((ende) => {
        viewer.appendChild(element('span', {
          slot: 'hotspot-' + l.id + '-' + ende,
          'data-position': position(l[ende]),
          'data-moonou-3d-anker': '',
          class: 'moonou-3d-anker',
          'aria-hidden': 'true'
        }));
      });
    });
    const svg = im('[data-moonou-3d-masse]');
    if (svg) {
      svg.replaceChildren();
      linien.forEach((l) => {
        const g = svgElement('g', { 'data-mass': l.id });
        g.appendChild(svgElement('line'));
        g.appendChild(svgElement('line'));
        g.appendChild(svgElement('line'));
        const text = svgElement('text', { 'text-anchor': 'middle' });
        text.textContent = l.text;
        g.appendChild(text);
        svg.appendChild(g);
      });
    }
    masseZeichnenPlanen();
  }

  function hotspotsNachfuehren() {
    if (!viewer || typeof viewer.updateHotspot !== 'function') return;
    const svg = im('[data-moonou-3d-masse]');
    massLinien(aktuelleMasse).forEach((l) => {
      viewer.updateHotspot({ name: 'hotspot-' + l.id + '-a', position: position(l.a) });
      viewer.updateHotspot({ name: 'hotspot-' + l.id + '-b', position: position(l.b) });
      const text = svg && svg.querySelector('[data-mass="' + l.id + '"] text');
      if (text) text.textContent = l.text;
    });
  }

  function masseZeichnenPlanen() {
    if (zeichnenFrame) cafAbbrechen(zeichnenFrame);
    zeichnenFrame = raf(masseZeichnen);
  }

  function leinwandPunkt(name) {
    if (!viewer || typeof viewer.queryHotspot !== 'function') return null;
    const h = viewer.queryHotspot(name);
    return h && h.canvasPosition ? h.canvasPosition : null;
  }

  function masseZeichnen() {
    zeichnenFrame = 0;
    const svg = im('[data-moonou-3d-masse]');
    if (!svg) return;
    const sichtbar = !!(zustand && zustand.masse && status === 'bereit' && viewer && aktuelleMasse);
    /* SVG-Elemente haben keine hidden-Eigenschaft: Attribut direkt setzen */
    if (sichtbar) svg.removeAttribute('hidden');
    else svg.setAttribute('hidden', '');
    if (!sichtbar) return;
    linien(aktuelleMasse).forEach((l) => {
      const g = svg.querySelector('[data-mass="' + l.id + '"]');
      const a = leinwandPunkt('hotspot-' + l.id + '-a');
      const b = leinwandPunkt('hotspot-' + l.id + '-b');
      if (!g || !a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const laenge = Math.hypot(dx, dy);
      const versteckt = laenge < 20 || a.z > 1 || b.z > 1;
      g.style.display = versteckt ? 'none' : '';
      if (versteckt) return;
      const nx = -dy / laenge;
      const ny = dx / laenge;
      const koordinaten = [
        [a.x, a.y, b.x, b.y],
        [a.x - nx * 4, a.y - ny * 4, a.x + nx * 4, a.y + ny * 4],
        [b.x - nx * 4, b.y - ny * 4, b.x + nx * 4, b.y + ny * 4]
      ];
      Array.prototype.forEach.call(g.querySelectorAll('line'), (linie, i) => {
        ['x1', 'y1', 'x2', 'y2'].forEach((attr, j) => linie.setAttribute(attr, String(koordinaten[i][j])));
      });
      let winkel = Math.atan2(dy, dx) * 180 / Math.PI;
      if (winkel > 90) winkel -= 180;
      if (winkel < -90) winkel += 180;
      const text = g.querySelector('text');
      if (text) {
        text.setAttribute('transform', 'translate(' + (a.x + b.x) / 2 + ',' + (a.y + b.y) / 2 + ') rotate(' + winkel + ')');
        text.setAttribute('y', '-8');
      }
    });
  }

  /* ---------- Einstiege in der Galerie ------------------------------------ */

  /* Pille ueber das Hauptbild: in den Wrapper des slider-element in
     media-gallery (snippets/product-media-gallery.liquid). Steht sie dort
     nicht, bleibt sie per CSS unsichtbar ([data-eingebaut] fehlt). */
  function pilleEinbauen(galerie) {
    if (!pille) return;
    const slider = galerie.querySelector('slider-element');
    const ziel = slider && slider.parentElement;
    if (!ziel) return;
    if (pille.parentNode !== ziel) ziel.appendChild(pille);
    pille.setAttribute('data-eingebaut', '');
  }

  /* 3D-Kachel in die Thumbnail-Liste (media-dots.product__thumbnails-list).
     MediaDots (theme.js) vergibt die Bild-Indizes beim init() nach der
     Reihenfolge der Kinder; init laeuft erst nach der ersten Interaktion
     oder 5 s. Stuende die Kachel vorher in der Liste, zeigten alle
     Thumbnails dahinter auf das falsche Bild. Darum erst nach init
     einfuegen (danach beachtet MediaDots die Kachel nicht mehr). */
  function kachelEinbauen(galerie) {
    const vorlage = wurzel.querySelector('template[data-moonou-3d-kachel]');
    const liste = galerie.querySelector('.product__thumbnails-list');
    if (!vorlage || !vorlage.content || !liste) return;
    if (liste.querySelector('[data-moonou-3d-open][data-einstieg="kachel"]')) return;
    if (typeof liste.init === 'function' && !liste.initialized && kachelVersuche < 40) {
      kachelVersuche++;
      if (!kachelTimer) kachelTimer = root.setTimeout(() => { kachelTimer = null; einbauen(); }, 300);
      return;
    }
    kachelVersuche = 0;
    const muster = vorlage.content.firstElementChild;
    if (!muster) return;
    const kachel = muster.cloneNode(true);
    const position = Math.max(1, parseInt(wurzel.getAttribute('data-kachel-position'), 10) || 2);
    const sichtbar = Array.prototype.filter.call(liste.children, (c) => !c.hidden);
    liste.insertBefore(kachel, sichtbar[position - 1] || null);
  }

  /* Beim Variantenwechsel ersetzt theme.js (ProductInfo.
     updateSourceFromDestination 'ProductGallery') den Inhalt von
     media-gallery: Pille und Kachel danach wieder einsetzen. */
  function galerieBeobachten(galerie) {
    if (beobachteteGalerie === galerie || typeof root.MutationObserver !== 'function') return;
    if (galerieBeobachter) galerieBeobachter.disconnect();
    beobachteteGalerie = galerie;
    galerieBeobachter = new root.MutationObserver(() => einbauen());
    galerieBeobachter.observe(galerie, { childList: true });
  }

  function einbauen() {
    if (!init()) return false;
    const galerie = bereich.querySelector('media-gallery') || doc.querySelector('media-gallery');
    if (!galerie) return false;
    pilleEinbauen(galerie);
    kachelEinbauen(galerie);
    galerieBeobachten(galerie);
    return true;
  }

  /* ---------- Start ------------------------------------------------------- */

  doc.addEventListener('click', (ev) => {
    const t = ev.target;
    if (!t || !t.closest) return;
    const oeffner = t.closest('[data-moonou-3d-open]');
    if (!oeffner) return;
    ev.preventDefault();
    oeffnen(oeffner.getAttribute('data-einstieg') || 'pille', oeffner);
  });

  function start() { einbauen(); }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
  doc.addEventListener('shopify:section:load', start);

  api.oeffnen = (einstieg) => oeffnen(einstieg);
  api.schliessen = schliessen;
  api.einbauen = einbauen;
})(typeof window !== 'undefined' ? window : globalThis);
